import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listWebsites,
  createWebsite,
  runIntake,
  getVerificationInfo,
  verifyWebsite,
} from "../api/websites.js";
import type {
  Website,
  IntakeResult,
  VerificationMethod,
} from "../api/types.js";

export function useWebsites(orgId: string | undefined) {
  return useQuery<{ websites: Website[] }>({
    queryKey: ["websites", orgId],
    queryFn: () => {
      if (!orgId) throw new Error("orgId is required");
      return listWebsites(orgId);
    },
    enabled: orgId != null,
  });
}

export function useFirstWebsite(orgId: string | undefined): Website | null {
  const { data } = useWebsites(orgId);
  return data?.websites[0] ?? null;
}

export function useCreateWebsite(orgId: string) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; website: Website }, Error, string>({
    mutationFn: (url: string) => createWebsite(orgId, url),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["websites", orgId] });
    },
  });
}

export function useRunIntake(websiteId: string) {
  const qc = useQueryClient();
  return useMutation<IntakeResult, Error>({
    mutationFn: () => runIntake(websiteId),
    onSuccess: (data) => {
      qc.setQueryData(["intake", websiteId], data);
    },
  });
}

export function useIntakeResult(websiteId: string) {
  return useQuery<IntakeResult>({
    queryKey: ["intake", websiteId],
    queryFn: () => runIntake(websiteId),
    enabled: false,
    staleTime: Infinity,
  });
}

export function useVerificationInfo(websiteId: string | undefined) {
  return useQuery({
    queryKey: ["verification", websiteId],
    queryFn: () => {
      if (!websiteId) throw new Error("websiteId is required");
      return getVerificationInfo(websiteId);
    },
    enabled: websiteId != null,
  });
}

export function useVerifyWebsite(websiteId: string) {
  const qc = useQueryClient();
  return useMutation<
    { ok: boolean; verified: boolean },
    Error,
    VerificationMethod
  >({
    mutationFn: (method) => verifyWebsite(websiteId, method),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["websites"] });
    },
  });
}
